import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { formatDate } from "@/lib/format";
import {
  TRIP_DATE_LIFECYCLE_BADGES,
  TRIP_DATE_LIFECYCLE_LABELS,
  OCCUPANCY_BADGES,
  OCCUPANCY_LABELS,
  type TripDateLifecycle,
  type OccupancyLevel,
} from "@ckgo2/shared/types";
import { CalendarRange, Users, ChevronRight } from "lucide-react";
import { enumLabel } from "@/i18n/enumLabels";

interface DepartureRow {
  id: string;
  tripId: string;
  startDate: string;
  endDate: string;
  capacity: number;
  bookedCount: number;
  waitlistCount: number;
  lifecycle: TripDateLifecycle;
  occupancy: OccupancyLevel;
  code?: string | null;
  garant?: { id: string; name: string; source: "departure" | "destination" } | null;
  tripTitle?: string | null;
  tripSlug?: string | null;
  guideName?: string | null;
  orderCount: number;
  paxBooked: number;
}

export default function DeparturesPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ["departures"],
    queryFn: () => api.get<{ data: DepartureRow[] }>("/api/admin/departures?limit=200"),
  });
  const rows = data?.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t("ops.departures.title")}
        </h1>
        <p className="mt-1 text-gray-600 dark:text-gray-400">{t("ops.departures.subtitle")}</p>
      </div>

      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <div className="flex h-48 items-center justify-center text-gray-500">
            {t("common.states.loading")}
          </div>
        ) : rows.length === 0 ? (
          <div className="flex h-48 flex-col items-center justify-center text-gray-500">
            <CalendarRange className="mb-2 h-10 w-10 text-gray-300" />
            <p>{t("ops.departures.empty")}</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
              <tr>
                {[
                  t("ops.departures.colTrip"),
                  t("ops.departures.colDates"),
                  t("ops.departures.colGuide"),
                  t("ops.departures.colBooked"),
                  t("ops.departures.colStatus"),
                  "",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                    {r.code && (
                      <span className="mr-2 rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        {r.code}
                      </span>
                    )}
                    {r.tripTitle ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    {formatDate(r.startDate)} – {formatDate(r.endDate)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{r.guideName ?? "—"}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    <span className="inline-flex items-center gap-1">
                      <Users className="h-3.5 w-3.5 text-gray-400" />
                      {r.paxBooked}/{r.capacity}
                      {r.waitlistCount > 0 && (
                        <span className="text-amber-600">
                          {" "}
                          {t("ops.departures.waitlistShort", { count: r.waitlistCount })}
                        </span>
                      )}
                    </span>
                    <span className="ml-1 text-xs text-gray-400">
                      {t("ops.departures.ordersCount", { count: r.orderCount })}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-1">
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-medium ${TRIP_DATE_LIFECYCLE_BADGES[r.lifecycle] ?? "bg-gray-100 text-gray-600"}`}
                      >
                        {enumLabel(TRIP_DATE_LIFECYCLE_LABELS, r.lifecycle)}
                      </span>
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-medium ${OCCUPANCY_BADGES[r.occupancy] ?? "bg-gray-100 text-gray-600"}`}
                        title={t("ops.departures.occupancyTitle")}
                      >
                        {enumLabel(OCCUPANCY_LABELS, r.occupancy)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/departures/${r.id}`}
                      className="btn-ghost btn-sm"
                      title={t("ops.departures.openVelin")}
                    >
                      {t("common.actions.open")} <ChevronRight className="h-4 w-4" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
